import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  Stack,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material'
import { Key as KeyIcon, ContentCopy as CopyIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { authService, MfaSetupResponse } from '../../services/authService'

interface MfaSetupDialogProps {
  open: boolean
  onClose: () => void
  onComplete: (backupCodes: string[]) => void
}

export const MfaSetupDialog: React.FC<MfaSetupDialogProps> = ({ open, onClose, onComplete }) => {
  const { t } = useTranslation('profile')
  const [activeStep, setActiveStep] = useState(0)
  const [setupData, setSetupData] = useState<MfaSetupResponse | null>(null)
  const [verificationCode, setVerificationCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [secretCopied, setSecretCopied] = useState(false)

  const steps = [t('mfa.setup.step1', 'QRコードをスキャン'), t('mfa.setup.step2', 'コードを確認')]

  useEffect(() => {
    if (open) {
      initSetup()
    } else {
      // ダイアログを閉じたらリセット
      setActiveStep(0)
      setSetupData(null)
      setVerificationCode('')
      setError(null)
      setSecretCopied(false)
    }
  }, [open])

  const initSetup = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await authService.setupMfa()
      setSetupData(data)
    } catch (err: any) {
      setError(err.response?.data?.error || 'MFAセットアップに失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleCopySecret = async () => {
    if (setupData?.secret) {
      await navigator.clipboard.writeText(setupData.secret)
      setSecretCopied(true)
      setTimeout(() => setSecretCopied(false), 2000)
    }
  }

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('6桁の認証コードを入力してください')
      return
    }

    try {
      setLoading(true)
      setError(null)
      const response = await authService.enableMfa(verificationCode)
      onComplete(response.backupCodes)
    } catch (err: any) {
      setError(err.response?.data?.error || '認証コードが正しくありません')
    } finally {
      setLoading(false)
    }
  }

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6)
    setVerificationCode(value)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('mfa.setup.title', '2段階認証のセットアップ')}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && <Alert severity="error">{error}</Alert>}

          {loading && !setupData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : activeStep === 0 ? (
            <Stack spacing={3}>
              <Typography variant="body2" color="text.secondary">
                {t(
                  'mfa.setup.scanInstructions',
                  'Google Authenticator、Microsoft Authenticator、Authyなどの認証アプリでQRコードをスキャンしてください。'
                )}
              </Typography>

              {setupData?.qrCodeUrl && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box
                    component="img"
                    src={setupData.qrCodeUrl}
                    alt="MFA QR Code"
                    sx={{ width: 200, height: 200 }}
                  />
                </Box>
              )}

              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <KeyIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  {t(
                    'mfa.setup.manualEntry',
                    'QRコードをスキャンできない場合は、以下のキーを手動で入力してください:'
                  )}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      bgcolor: 'white',
                      p: 1,
                      borderRadius: 0.5,
                      flex: 1,
                      wordBreak: 'break-all',
                    }}
                  >
                    {setupData?.secret}
                  </Typography>
                  <Button size="small" startIcon={<CopyIcon />} onClick={handleCopySecret}>
                    {secretCopied ? t('mfa.copied', 'コピー済み') : t('mfa.copy', 'コピー')}
                  </Button>
                </Box>
              </Box>

              <Button variant="contained" onClick={() => setActiveStep(1)} fullWidth>
                {t('mfa.setup.next', '次へ')}
              </Button>
            </Stack>
          ) : (
            <Stack spacing={3}>
              <Typography variant="body2" color="text.secondary">
                {t(
                  'mfa.setup.verifyCode',
                  '認証アプリに表示されている6桁のコードを入力してください。'
                )}
              </Typography>

              <TextField
                label={t('mfa.setup.codeLabel', '認証コード')}
                value={verificationCode}
                onChange={handleCodeChange}
                placeholder="000000"
                inputProps={{
                  maxLength: 6,
                  style: {
                    textAlign: 'center',
                    fontSize: '1.5rem',
                    letterSpacing: '0.5rem',
                    fontFamily: 'monospace',
                  },
                }}
                fullWidth
                autoFocus
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant="outlined" onClick={() => setActiveStep(0)} disabled={loading}>
                  {t('mfa.setup.back', '戻る')}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleVerify}
                  disabled={loading || verificationCode.length !== 6}
                  fullWidth
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    t('mfa.setup.verify', '確認して有効化')
                  )}
                </Button>
              </Box>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('common.cancel', 'キャンセル')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
